import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { resourcesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Loader2, Eye, EyeOff, FileText, BookOpen, BarChart3, Mail, Lightbulb, Layers, Lock } from 'lucide-react';
import { CATEGORY_NAMES, CATEGORY_COLOR_MAP } from '../../constants/resourceCategories';
import Pagination from '../../components/shared/Pagination';

const NAVY  = '#1e3a5f';
const ORANGE = '#ea580c';
const PAGE_SIZE = 20;

const STATUS_META = {
  published: { label: 'Published', bg: '#dcfce7', color: '#166534' },
  draft:     { label: 'Draft',     bg: '#fef9c3', color: '#854d0e' },
  archived:  { label: 'Archived',  bg: '#f1f5f9', color: '#64748b' },
};

// Built-in / code-driven resources that aren't in MongoDB
const FEATURED_DEFS = [
  { id: '__pricing_guide__', title: 'Partner Pricing Guide', category: 'Sales Enablement', slug: 'pricing-guide', _key: 'pricing-guide' },
];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
}

export default function ResourcesAdmin() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [resources, setResources]         = useState([]);
  const [pagination, setPagination]       = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage]                   = useState(1);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [featuredVis, setFeaturedVis]     = useState({});
  const [savingVis, setSavingVis]         = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, page, limit: PAGE_SIZE };
      if (search)         params.search = search;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const [resRes, visRes] = await Promise.all([
        resourcesAPI.list(params),
        resourcesAPI.getFeaturedVisibility(),
      ]);
      const d = resRes.data || {};
      setResources(d.items || []);
      setPagination({ total: d.total || 0, page: d.page || 1, pages: d.pages || 1 });
      setFeaturedVis(visRes.data || {});
    } catch { toast.error('Failed to load resources'); }
    finally { setLoading(false); }
  }, [search, statusFilter, categoryFilter, page]);

  useEffect(() => { load(); }, [load]);

  // Reset page when filters change
  const handleSearch   = (val) => { setSearch(val);         setPage(1); };
  const handleStatus   = (val) => { setStatusFilter(val);   setPage(1); };
  const handleCategory = (val) => { setCategoryFilter(val); setPage(1); };

  const handleStatusCycle = async (resource) => {
    const next = { published: 'draft', draft: 'archived', archived: 'published' };
    const newStatus = next[resource.status] || 'draft';
    try {
      await resourcesAPI.setStatus(resource.id, newStatus);
      toast.success(`${resource.title}: ${resource.status} → ${newStatus}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
  };

  const handleSetStatus = async (resource, status) => {
    try {
      await resourcesAPI.setStatus(resource.slug || resource.id, status);
      toast.success(`Status set to ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await resourcesAPI.remove(deleteTarget.slug || deleteTarget.id);
      toast.success(`"${deleteTarget.title}" permanently deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const toggleFeaturedVis = async (key) => {
    const newVis = { ...featuredVis, [key]: !featuredVis[key] };
    setSavingVis(s => ({ ...s, [key]: true }));
    try {
      await resourcesAPI.setFeaturedVisibility(newVis);
      setFeaturedVis(newVis);
      toast.success(`Pricing Guide ${newVis[key] ? 'visible' : 'hidden'}`);
    } catch { toast.error('Failed to update visibility'); }
    finally { setSavingVis(s => ({ ...s, [key]: false })); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>Resource Hub Admin</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            Manage published assets, drafts and visibility. {isAdmin ? 'Admins can edit, archive and permanently delete.' : 'Managers can toggle Published/Draft status.'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => navigate('/admin/resources/new')}
            style={{ background: NAVY, color: 'white' }} className="gap-1.5">
            <Plus size={14} /> New Resource
          </Button>
        )}
      </div>

      {/* Built-in resources */}
      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="px-5 py-3" style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
          <p className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5" style={{ color: NAVY }}>
            <Lock size={12} /> Built-in resources
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            These resources are code-driven. Content is managed through the application. Visibility only.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Visible in Resources Hub</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {FEATURED_DEFS.map(f => (
              <TableRow key={f.id}>
                <TableCell className="font-semibold text-sm" style={{ color: NAVY }}>{f.title}</TableCell>
                <TableCell>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ background: (CATEGORY_COLOR_MAP[f.category] || '#64748b') + '18', color: CATEGORY_COLOR_MAP[f.category] || '#64748b' }}>
                    {f.category}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#f1f5f9', color: '#64748b' }}>
                    Built-in
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={featuredVis[f._key] !== false}
                      onCheckedChange={() => isAdmin && toggleFeaturedVis(f._key)}
                      disabled={!isAdmin || savingVis[f._key]}
                    />
                    <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {featuredVis[f._key] !== false ? 'Visible' : 'Hidden'}
                    </span>
                    {savingVis[f._key] && <Loader2 size={12} className="animate-spin" />}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
          <Input placeholder="Search resources…" value={search} onChange={e => handleSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={handleStatus}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={handleCategory}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORY_NAMES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Content resources table */}
      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between" style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
          <p className="text-xs font-bold uppercase tracking-wide" style={{ color: NAVY }}>
            Content resources ({pagination.total})
          </p>
        </div>
        {loading ? (
          <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : resources.length === 0 ? (
          <div className="p-10 text-center">
            <FileText size={24} className="mx-auto mb-2 opacity-20" />
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No resources found.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {resources.map(r => (
                <TableRow key={r.id} className="data-table-row">
                  <TableCell>
                    <p className="font-semibold text-sm" style={{ color: NAVY }}>{r.title}</p>
                    {r.description && <p className="text-xs mt-0.5 max-w-xs truncate" style={{ color: 'hsl(var(--muted-fg))' }}>{r.description}</p>}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ background: (CATEGORY_COLOR_MAP[r.category] || '#64748b') + '18', color: CATEGORY_COLOR_MAP[r.category] || '#64748b' }}>
                      {r.category}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={s => handleSetStatus(r, s)}>
                      <SelectTrigger className="h-7 text-xs w-28 border-0 bg-transparent p-0 focus:ring-0">
                        <StatusBadge status={r.status} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        {isAdmin && <SelectItem value="archived">Archived</SelectItem>}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-mono" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {r.slug || r.id.slice(0, 8) + '…'}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                        onClick={() => navigate(`/admin/resources/${r.slug || r.id}/edit`)}>
                        <Pencil size={12} />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          style={{ color: 'hsl(var(--danger))' }}
                          onClick={() => setDeleteTarget(r)}>
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        page={pagination.page}
        pages={pagination.pages}
        total={pagination.total}
        limit={PAGE_SIZE}
        onChange={setPage}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={16} /> Permanently delete resource
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
              This will <strong>permanently delete</strong> "{deleteTarget?.title}" from the database. This cannot be undone.
            </p>
            <div className="rounded-lg p-3 text-xs" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
              Consider setting the status to <strong>Archived</strong> instead if you may need this resource again.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}>
              {deleting ? <Loader2 size={14} className="animate-spin" /> : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
